import React, { useState, useEffect } from "react";
import {
	ChevronDown,
	Loader2,
	Info,
	Upload,
	FileText,
	XCircle,
	Clock,
	CheckCircle,
} from "lucide-react";
import { AuthContainer } from "./AuthContainer";
import { useAuth } from "./AuthContext";
import {
	useGetExtra,
	useUpdateExtra,
	useGetCountries,
	useGetOccupations,
} from "../api";
import { useOccupationProof } from "../api/occupationProof/useOccupationProof";
import { LANGUAGE_LEVELS } from "../constants/languageLevels";
import { useWorkPermitProof } from "../api/workPermitProof/useWorkPermitProof";
import { useCurrentUser } from "../api/auth/useCurrentUser";
import type { Extra } from "../types/extra.d";
import { toLocalDateString } from "../utils";
import { useTranslation } from "../i18n/TranslationContext";
import { COMPANY_OPTIONS } from "../constants/companies";
import { AUSWEIS_COLOR_OPTIONS } from "../constants/ausweisColors";
import { useDataAccuracyConfirmation } from "../hooks/useDataAccuracyConfirmation";

export function ProfileInformation() {
	const { t, language } = useTranslation();
	// Get current user to fetch their ID
	const { user } = useCurrentUser();
	const { setCurrentStep, updateAuthData, authData } = useAuth();
	const { countries, loading: countriesLoading } = useGetCountries();
	const { occupations, loading: occupationsLoading } = useGetOccupations();

	// Fetch extra data from API with dynamic user ID
	const {
		extra,
		loading: apiLoading,
		error: apiError,
		refetch,
	} = useGetExtra(user?.extraId);

	// Hook for updating extra data
	const { updateExtra, loading: updateLoading } = useUpdateExtra();
	const { proofInfo, deleteProof } = useOccupationProof(user?.extraId);
	const { proofInfo: workPermitInfo, deleteProof: deleteWorkPermitProof } =
		useWorkPermitProof(user?.extraId);

	const [formData, setFormData] = useState<Partial<Extra>>({
		first_name: authData.firstName || "",
		last_name: authData.lastName || "",
		email: authData.email || "",
		mobile_number: authData.phone || "",
		street: "",
		postcode: "",
		city: "",
		date_of_birth: "",
		city_of_birth: "",
		nationality: undefined,
		language_level: undefined,
		company: "",
		department: "",
		flughafenausweisnummer: "",
		ausweis_color: "",
		main_occupation_id: undefined,
		id_number: "",
		accepts_64_day_rule: false,
	});
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [isLoadingData, setIsLoadingData] = useState(true);
	// Navigation warning modal for unsaved changes
	const [showNavigationWarningModal, setShowNavigationWarningModal] =
		useState(false);
	const [navigationTarget, setNavigationTarget] = useState<
		"occupation-proof" | "work-permit-proof" | null
	>(null);
	// Confirmation modal for occupation change
	const [showOccupationChangeModal, setShowOccupationChangeModal] =
		useState(false);
	const [pendingOccupationId, setPendingOccupationId] = useState<
		number | undefined
	>(undefined);
	// Confirmation modal for nationality change (work permit)
	const [showNationalityChangeModal, setShowNationalityChangeModal] =
		useState(false);
	const [pendingNationalityId, setPendingNationalityId] = useState<
		number | undefined
	>(undefined);
	// Info modal for ID number explanation
	const [showIdInfoModal, setShowIdInfoModal] = useState(false);
	// Info modal for 64-day rule explanation
	const [show64DayRuleInfoModal, setShow64DayRuleInfoModal] = useState(false);
	// Data accuracy acknowledgment with localStorage persistence
	const { acknowledgesDataAccuracy, setAcknowledgesDataAccuracy } =
		useDataAccuracyConfirmation("profile", user?.extraId);

	// Use countries from backend, sorted alphabetically
	const nationalities = countries.map((country) => country.nationality).sort();

	// Use actual enum values, not translations, to match backend expectations
	// Import language levels from constants
	const germanLevels = LANGUAGE_LEVELS;

	// Update form data when API data is loaded
	useEffect(() => {
		if (extra && !apiLoading) {
			const sanitizedData: Partial<Extra> = {};
			const stringFields = [
				"first_name",
				"last_name",
				"email",
				"mobile_number",
				"street",
				"postcode",
				"city",
				"date_of_birth",
				"city_of_birth",
				"company",
				"department",
				"flughafenausweisnummer",
				"ausweis_color",
				"id_number",
			];

			for (const [key, value] of Object.entries(extra)) {
				if (value === null && stringFields.includes(key)) {
					(sanitizedData as any)[key] = "";
				} else if (
					key === "language_level" &&
					(value === null || value === undefined)
				) {
					// Ensure language_level is undefined when null/undefined from backend
					(sanitizedData as any)[key] = undefined;
				} else if (
					key === "nationality" &&
					(value === null || value === undefined)
				) {
					// Ensure nationality is undefined when null/undefined from backend
					(sanitizedData as any)[key] = undefined;
				} else {
					(sanitizedData as any)[key] = value;
				}
			}

			setFormData({
				...sanitizedData,
				first_name: authData.firstName || sanitizedData.first_name || "",
				last_name: authData.lastName || sanitizedData.last_name || "",
				email: authData.email || sanitizedData.email || "",
				mobile_number: authData.phone || sanitizedData.mobile_number || "",
				date_of_birth: sanitizedData.date_of_birth
					? formatDateForInput(sanitizedData.date_of_birth)
					: "",
			});
			setIsLoadingData(false);
		} else if (!apiLoading && !extra) {
			setIsLoadingData(false);
		}
	}, [extra, apiLoading, authData]);

	// Helper function to format date for input field
	const formatDateForInput = (dateString: string): string => {
		if (!dateString) return "";
		return toLocalDateString(dateString);
	};

	const validateForm = () => {
		const newErrors: Record<string, string> = {};

		// Personal Information
		if (!formData.first_name?.trim()) {
			newErrors.first_name = t("profileInformation.errors.firstNameRequired");
		}
		if (!formData.last_name?.trim()) {
			newErrors.last_name = t("profileInformation.errors.lastNameRequired");
		}
		if (!formData.date_of_birth) {
			newErrors.date_of_birth = t(
				"profileInformation.errors.dateOfBirthRequired"
			);
		} else {
			// Validate that date of birth is not in the future
			const selectedDate = new Date(formData.date_of_birth);
			const today = new Date();
			today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

			if (selectedDate > today) {
				newErrors.date_of_birth = t(
					"profileInformation.errors.dateOfBirthFuture"
				);
			}

			// Validate that date is not unreasonably old (before year 1900)
			const minDate = new Date("1900-01-01");
			if (selectedDate < minDate) {
				newErrors.date_of_birth =
					"Bitte geben Sie ein gültiges Geburtsdatum ein (nach 1900).";
			}

			// Check if date is valid
			if (isNaN(selectedDate.getTime())) {
				newErrors.date_of_birth =
					"Ungültiges Datumsformat. Bitte verwenden Sie das Format TT.MM.JJJJ.";
			}
		}
		if (!formData.nationality) {
			newErrors.nationality = t(
				"profileInformation.errors.nationalityRequired"
			);
		}
		if (!formData.language_level) {
			newErrors.language_level = t(
				"profileInformation.errors.germanLevelRequired"
			);
		}
		if (!formData.amount_flights) {
			newErrors.amount_flights = "Anzahl der Flüge ist erforderlich";
		}

		// ID Number - required for external users only
		if (user?.internal_extra !== true && !formData.id_number?.trim()) {
			newErrors.id_number = "ID-Nummer ist erforderliche";
		}

		// Occupation - required for external users
		if (user?.internal_extra !== true && !formData.main_occupation_id) {
			newErrors.main_occupation_id = "Beschäftigung ist erforderliche";
		}

		// Airport ID fields - Only validate for internal users
		if (user?.internal_extra) {
			if (!formData.flughafenausweisnummer?.trim()) {
				newErrors.flughafenausweisnummer = t(
					"profileInformation.errors.flughafenausweisnummerRequired"
				);
			}
			if (!formData.ausweis_color?.trim()) {
				newErrors.ausweis_color = t(
					"profileInformation.errors.ausweisfarbeRequired"
				);
			}
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	// Handle confirmed occupation change
	const handleConfirmOccupationChange = async () => {
		// Delete the existing proof if it exists
		if (proofInfo) {
			try {
				await deleteProof();
				console.log("Occupation proof deleted due to occupation change");
			} catch (error) {
				console.error("Failed to delete occupation proof:", error);
				// Continue with occupation change even if proof deletion fails
			}
		}

		// Update the occupation
		setFormData((prev) => ({
			...prev,
			main_occupation_id: pendingOccupationId,
		}));
		setShowOccupationChangeModal(false);
		setPendingOccupationId(undefined);

		// Clear any occupation-related errors
		if (errors.main_occupation_id) {
			setErrors((prev) => ({ ...prev, main_occupation_id: "" }));
		}
	};

	// Handle cancelled occupation change
	const handleCancelOccupationChange = () => {
		setShowOccupationChangeModal(false);
		setPendingOccupationId(undefined);
	};

	// Handle confirmed nationality change
	const handleConfirmNationalityChange = async () => {
		// Delete the existing work permit if it exists
		if (workPermitInfo) {
			try {
				await deleteWorkPermitProof();
				console.log("Work permit proof deleted due to nationality change");
			} catch (error) {
				console.error("Failed to delete work permit proof:", error);
				// Continue with nationality change even if proof deletion fails
			}
		}

		// Update the nationality
		setFormData((prev) => ({
			...prev,
			nationality: pendingNationalityId,
		}));
		setShowNationalityChangeModal(false);
		setPendingNationalityId(undefined);

		// Clear any nationality-related errors
		if (errors.nationality) {
			setErrors((prev) => ({ ...prev, nationality: "" }));
		}
	};

	// Handle cancelled nationality change
	const handleCancelNationalityChange = () => {
		setShowNationalityChangeModal(false);
		setPendingNationalityId(undefined);
	};

	// Check if there are unsaved changes
	const hasUnsavedChanges = () => {
		if (!extra) return false;

		// Compare key fields that might have changed
		const fieldsToCheck = [
			"first_name",
			"last_name",
			"date_of_birth",
			"nationality",
			"language_level",
			"company",
			"department",
			"flughafenausweisnummer",
			"ausweis_color",
			"main_occupation_id",
		];

		for (const field of fieldsToCheck) {
			const currentValue = formData[field as keyof typeof formData];
			const originalValue = extra[field as keyof typeof extra];

			// Handle date formatting comparison
			if (field === "date_of_birth") {
				const currentDate = currentValue || "";
				const originalDate = originalValue
					? formatDateForInput(originalValue as string)
					: "";
				if (currentDate !== originalDate) {
					return true;
				}
			} else {
				// Handle null/undefined comparisons
				const normalizedCurrent =
					currentValue === null || currentValue === undefined
						? ""
						: currentValue;
				const normalizedOriginal =
					originalValue === null || originalValue === undefined
						? ""
						: originalValue;

				if (normalizedCurrent !== normalizedOriginal) {
					return true;
				}
			}
		}

		return false;
	};

	// Check if occupation proof upload should be shown (only for students)
	const shouldShowOccupationProof = () => {
		if (!formData.main_occupation_id) return false;

		// Get the selected occupation
		const selectedOccupation = occupations.find(
			(occ) => occ.id === formData.main_occupation_id
		);

		// Show occupation proof for students and high school students only
		if (selectedOccupation?.name) {
			const occupationName = selectedOccupation.name.toLowerCase();
			return (
				occupationName.includes("student") || occupationName.includes("schüler")
			);
		}

		return false;
	};

	// Navigate to occupation proof
	const navigateToOccupationProof = () => {
		if (hasUnsavedChanges()) {
			setNavigationTarget("occupation-proof");
			setShowNavigationWarningModal(true);
		} else {
			// Save current form data to auth context before navigating
			updateAuthData({
				firstName: formData.first_name,
				lastName: formData.last_name,
				email: formData.email,
				phone: formData.mobile_number,
			});
			// Navigate to occupation proof screen
			setCurrentStep(7); // Assuming occupation proof is step 7
		}
	};

	// Check if work permit upload should be shown (only for non-EU nationals)
	const shouldShowWorkPermitProof = () => {
		if (!formData.nationality) return false;

		// Get the selected country
		const selectedCountry = countries.find(
			(country) => country.country_id === formData.nationality
		);

		// Show work permit proof for non-EU nationals only
		return selectedCountry && !selectedCountry.is_eu;
	};

	// Navigate to work permit proof
	const navigateToWorkPermitProof = () => {
		if (hasUnsavedChanges()) {
			setNavigationTarget("work-permit-proof");
			setShowNavigationWarningModal(true);
		} else {
			// Save current form data to auth context before navigating
			updateAuthData({
				firstName: formData.first_name,
				lastName: formData.last_name,
				email: formData.email,
				phone: formData.mobile_number,
			});
			// Navigate to work permit proof screen
			setCurrentStep(8); // Assuming work permit proof is step 8
		}
	};

	// Handle save and navigate
	const handleSaveAndNavigate = async () => {
		if (validateForm()) {
			// Filter out email and mobile_number as they should not be editable
			const { email, mobile_number, ...editableFormData } = formData;
			// Add default values for fields not collected during signup
			const dataToSubmit = {
				...editableFormData,
				sozialversicherung: editableFormData.sozialversicherung || "",
				iban: editableFormData.iban || "",
				bic: editableFormData.bic || "",
			};

			if (!user?.extraId) {
				console.error("User extraId is not set");
				setShowNavigationWarningModal(false);
				return;
			}

			// Send data to API
			const updatedExtra = await updateExtra(user?.extraId, dataToSubmit);
			if (updatedExtra) {
				// Update auth data for success screen
				updateAuthData({
					firstName: formData.first_name,
					lastName: formData.last_name,
					dateOfBirth: formData.date_of_birth,
					nationality: formData.nationality?.toString(),
					germanLevel: formData.language_level,
				});

				// Close modal and navigate
				setShowNavigationWarningModal(false);

				// Navigate based on target
				if (navigationTarget === "work-permit-proof") {
					setCurrentStep(8); // Work permit proof screen
				} else if (navigationTarget === "occupation-proof") {
					setCurrentStep(7); // Occupation proof screen
				}
				// If navigationTarget is null, we don't navigate anywhere
			}
		} else {
			setShowNavigationWarningModal(false);
		}
	};

	// Handle input changes with occupation proof check
	const handleInputChange = (field: keyof Partial<Extra>, value: any) => {
		// Special handling for occupation changes when proof exists
		if (
			field === "main_occupation_id" &&
			proofInfo &&
			value !== formData.main_occupation_id
		) {
			// If changing occupation and there's existing proof, show warning
			setPendingOccupationId(value ? parseInt(value) : undefined);
			setShowOccupationChangeModal(true);
			return;
		}

		// Special handling for nationality changes when work permit exists
		if (
			field === "nationality" &&
			workPermitInfo &&
			value !== formData.nationality
		) {
			const newCountry = countries.find((c) => c.country_id === value);
			const oldCountry = countries.find(
				(c) => c.country_id === formData.nationality
			);

			// Check if changing from non-EU to EU or vice versa
			if (newCountry && oldCountry && newCountry.is_eu !== oldCountry.is_eu) {
				setPendingNationalityId(value);
				setShowNationalityChangeModal(true);
				return;
			}
		}

		setFormData((prev) => ({ ...prev, [field]: value }));
		// Clear error when user starts typing
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: "" }));
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (validateForm()) {
			// Filter out email and mobile_number as they should not be editable
			const { email, mobile_number, ...editableFormData } = formData;
			// Add default values for fields not collected during signup
			const dataToSubmit = {
				...editableFormData,
				sozialversicherung: editableFormData.sozialversicherung || "",
				iban: editableFormData.iban || "",
				bic: editableFormData.bic || "",
			};

			if (!user?.extraId) {
				console.error("User extraId is not set");
				return;
			}

			// Send data to API
			const updatedExtra = await updateExtra(user?.extraId, dataToSubmit);
			if (updatedExtra) {
				// Update auth data for success screen
				updateAuthData({
					firstName: formData.first_name,
					lastName: formData.last_name,
					dateOfBirth: formData.date_of_birth,
					nationality: formData.nationality?.toString(),
					germanLevel: formData.language_level,
				});
				setCurrentStep(7); // Go to success screen
			}
		}
	};

	// Show loading state while fetching data
	if (isLoadingData && apiLoading) {
		return (
			<AuthContainer showProgress>
				<div className="text-center">
					<h1 className="text-[28px] font-bold text-[var(--ucm-text-dark)] mb-8 leading-tight">
						{t("profileInformation.loadingTitle")}
					</h1>
					<div className="flex justify-center">
						<Loader2 className="w-8 h-8 animate-spin text-[var(--ucm-primary-teal)]" />
					</div>
				</div>
			</AuthContainer>
		);
	}

	return (
		<>
			{/* Occupation Change Confirmation Modal */}
			{showOccupationChangeModal && (
				<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
					<div className="bg-white rounded-2xl p-6 max-w-md w-full">
						<h2 className="text-xl font-bold text-[var(--ucm-text-dark)] mb-4">
							Beschäftigung ändern
						</h2>
						<p className="text-[var(--ucm-text-medium)] mb-6">
							Wenn Sie Ihre Beschäftigung ändern, wird Ihr vorhandener
							Beschäftigungsnachweis gelöscht. Sie müssen einen neuen Nachweis
							für die ausgewählte Beschäftigung hochladen.
						</p>
						<div className="flex gap-3">
							<button
								onClick={handleCancelOccupationChange}
								className="flex-1 px-4 py-2 border border-[var(--ucm-border-medium)] rounded-xl text-[var(--ucm-text-dark)] hover:bg-gray-50 transition-colors"
							>
								{t("common.cancel")}
							</button>
							<button
								onClick={handleConfirmOccupationChange}
								className="flex-1 px-4 py-2 bg-[var(--ucm-warning-orange)] text-white rounded-xl hover:bg-opacity-90 transition-colors"
							>
								Änderung bestätigen
							</button>
						</div>
					</div>
				</div>
			)}

			<AuthContainer showProgress>
				<div className="text-center mb-8">
					<h1 className="text-[28px] font-bold text-[var(--ucm-text-dark)] mb-8 leading-tight">
						{t("profileInformation.title")}
					</h1>
				</div>

				<form onSubmit={handleSubmit} className="space-y-6">
					<div>
						<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
							{t("profileInformation.fields.firstName")}{" "}
							<span className="text-red-500">*</span>
						</label>
						<div className="relative">
							<input
								type="text"
								value={formData.first_name}
								onChange={(e) =>
									handleInputChange("first_name", e.target.value)
								}
								className={`w-full h-14 px-4 border rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent ${
									errors.first_name
										? "border-[var(--ucm-error-red)]"
										: "border-[var(--ucm-border-light)]"
								}`}
								placeholder={t("profileInformation.placeholders.firstName")}
							/>
						</div>
						{errors.first_name && (
							<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
								{errors.first_name}
							</p>
						)}
					</div>

					<div>
						<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
							{t("profileInformation.fields.lastName")}{" "}
							<span className="text-red-500">*</span>
						</label>
						<div className="relative">
							<input
								type="text"
								value={formData.last_name}
								onChange={(e) => handleInputChange("last_name", e.target.value)}
								className={`w-full h-14 px-4 border rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent ${
									errors.last_name
										? "border-[var(--ucm-error-red)]"
										: "border-[var(--ucm-border-light)]"
								}`}
								placeholder={t("profileInformation.placeholders.lastName")}
							/>
						</div>
						{errors.last_name && (
							<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
								{errors.last_name}
							</p>
						)}
					</div>

					<div>
						<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
							{t("profileInformation.fields.email")}{" "}
							<span className="text-red-500">*</span>
						</label>
						<div className="relative">
							<input
								type="email"
								value={formData.email}
								readOnly
								className="w-full h-14 px-4 border border-[var(--ucm-border-light)] rounded-xl bg-gray-50 cursor-not-allowed"
								placeholder={t("profileInformation.placeholders.email")}
							/>
						</div>
					</div>

					<div>
						<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
							{t("profileInformation.fields.phoneNumber")}{" "}
							<span className="text-red-500">*</span>
						</label>
						<div className="relative">
							<input
								type="tel"
								value={formData.mobile_number}
								readOnly
								className="w-full h-14 px-4 border border-[var(--ucm-border-light)] rounded-xl bg-gray-50 cursor-not-allowed"
								placeholder={t("profileInformation.placeholders.phoneNumber")}
							/>
						</div>
					</div>

					<div>
						<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
							{t("profileInformation.fields.dateOfBirth")}{" "}
							<span className="text-red-500">*</span>
						</label>
						<div className="relative">
							<input
								type="date"
								value={formData.date_of_birth}
								min="1900-01-01"
								max={toLocalDateString(new Date())} // Prevent selecting future dates
								onChange={(e) =>
									handleInputChange("date_of_birth", e.target.value)
								}
								className={`w-full h-14 px-4 border rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent ${
									errors.date_of_birth
										? "border-[var(--ucm-error-red)]"
										: "border-[var(--ucm-border-light)]"
								}`}
							/>
						</div>
						{errors.date_of_birth && (
							<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
								{errors.date_of_birth}
							</p>
						)}
					</div>

					<div>
						<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
							{t("profileInformation.fields.nationality")}{" "}
							<span className="text-red-500">*</span>
						</label>
						<div className="relative">
							{countriesLoading ? (
								<div className="w-full h-14 px-4 border border-[var(--ucm-border-light)] rounded-xl bg-white flex items-center justify-center">
									<Loader2 className="w-5 h-5 animate-spin text-[var(--ucm-primary-teal)]" />
								</div>
							) : (
								<select
									value={formData.nationality}
									onChange={(e) =>
										handleInputChange("nationality", e.target.value)
									}
									className={`w-full h-14 px-4 pr-12 border rounded-xl bg-white appearance-none transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent ${
										errors.nationality
											? "border-[var(--ucm-error-red)]"
											: "border-[var(--ucm-border-light)]"
									}`}
								>
									<option value="">
										{t("profileInformation.placeholders.selectNationality")}
									</option>
									{nationalities.map((nationality) => (
										<option key={nationality} value={nationality}>
											{nationality}
										</option>
									))}
								</select>
							)}
							{!countriesLoading && (
								<ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] w-5 h-5 pointer-events-none" />
							)}
						</div>
						{errors.nationality && (
							<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
								{errors.nationality}
							</p>
						)}
					</div>

					{/* ID-Nummer - Only for external users */}
					{user?.internal_extra !== true && (
						<div>
							<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
								ID-Nummer <span className="text-red-500">*</span>
							</label>
							<div className="relative">
								<input
									type="text"
									value={formData.id_number}
									onChange={(e) =>
										handleInputChange("id_number", e.target.value)
									}
									className={`w-full h-14 px-4 pr-10 border rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent ${
										errors.id_number
											? "border-[var(--ucm-error-red)]"
											: "border-[var(--ucm-border-light)]"
									}`}
									placeholder="Personalausweis- oder Reisepassnummer"
									maxLength={50}
								/>
								<Info
									className="absolute right-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[var(--ucm-text-light)] cursor-pointer hover:text-[var(--ucm-primary-teal)] transition-colors"
									onClick={() => setShowIdInfoModal(true)}
								/>
							</div>
							{errors.id_number && (
								<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
									{errors.id_number}
								</p>
							)}
						</div>
					)}

					{/* Work permit proof button - Only for non-EU nationals */}
					{user?.internal_extra !== true && shouldShowWorkPermitProof() && (
						<div>
							<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
								Arbeitserlaubnis
							</label>
							<div
								onClick={navigateToWorkPermitProof}
								className="w-full px-4 py-3 border border-[var(--ucm-border-light)] rounded-xl cursor-pointer hover:border-[var(--ucm-primary-teal)] transition-colors flex items-center justify-between group"
							>
								<div className="flex items-center gap-3">
									{workPermitInfo ? (
										<>
											<FileText className="w-5 h-5 text-[var(--ucm-primary-teal)]" />
											<span className="text-[var(--ucm-text-dark)]">
												{workPermitInfo.file_name}
											</span>
										</>
									) : (
										<>
											<Upload className="w-5 h-5 text-[var(--ucm-text-medium)] group-hover:text-[var(--ucm-primary-teal)]" />
											<span className="text-[var(--ucm-text-medium)] group-hover:text-[var(--ucm-primary-teal)]">
												Arbeitserlaubnis hochladen
											</span>
										</>
									)}
								</div>
								<ChevronDown className="w-5 h-5 text-[var(--ucm-text-medium)] transform -rotate-90" />
							</div>
							{!workPermitInfo ? (
								<p className="mt-2 text-xs text-[var(--ucm-text-light)]">
									Als Nicht-EU-Bürger benötigen wir Ihre Arbeitserlaubnis. Bitte
									laden Sie den Nachweis hoch, um sich auf Events bewerben zu
									können.
								</p>
							) : (
								<div
									className={`mt-2 px-3 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${
										workPermitInfo.status === "pending"
											? "bg-yellow-100 text-yellow-800"
											: workPermitInfo.status === "approved"
												? "bg-green-100 text-green-800"
												: "bg-red-100 text-red-800"
									}`}
								>
									{workPermitInfo.status === "approved" && (
										<CheckCircle className="w-3 h-3 text-green-800" />
									)}

									{workPermitInfo.status === "pending" && (
										<Clock className="w-3 h-3 text-yellow-800" />
									)}
									{workPermitInfo.status === "rejected" && (
										<XCircle className="w-3 h-3 text-red-800" />
									)}

									{workPermitInfo.status === "pending" && "Ausstehend"}
									{workPermitInfo.status === "approved" && "Genehmigt"}
									{workPermitInfo.status === "rejected" && "Abgelehnt"}
								</div>
							)}
						</div>
					)}

					<div>
						<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
							{t("profileInformation.fields.germanLevel")}{" "}
							<span className="text-red-500">*</span>
						</label>
						<div className="relative">
							<select
								value={formData.language_level}
								onChange={(e) =>
									handleInputChange("language_level", e.target.value)
								}
								className={`w-full h-14 px-4 pr-12 border rounded-xl bg-white appearance-none transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent ${
									errors.language_level
										? "border-[var(--ucm-error-red)]"
										: "border-[var(--ucm-border-light)]"
								}`}
							>
								<option value="">
									{t("profileInformation.placeholders.selectGermanLevel")}
								</option>
								{germanLevels.map((level) => (
									<option key={level} value={level}>
										{level}
									</option>
								))}
							</select>
							<ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] w-5 h-5 pointer-events-none" />
						</div>
						{errors.language_level && (
							<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
								{errors.language_level}
							</p>
						)}
					</div>

					{/* Amount of Flights - Required for all users */}
					<div>
						<label
							htmlFor="amountFlights"
							className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2"
						>
							Anzahl der Flüge im letzten Jahr{" "}
							<span className="text-red-500">*</span>
						</label>
						<div className="relative">
							<select
								id="amountFlights"
								value={formData.amount_flights || ""}
								onChange={(e) =>
									handleInputChange("amount_flights", e.target.value)
								}
								className={`w-full h-14 px-4 pr-12 border rounded-xl bg-white appearance-none transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent ${
									errors.amount_flights
										? "border-[var(--ucm-error-red)]"
										: "border-[var(--ucm-border-light)]"
								}`}
							>
								<option value="">Bitte wählen</option>
								<option value="0-2">0-2 Flüge</option>
								<option value="3-4">3-4 Flüge</option>
								<option value="5-8">5-8 Flüge</option>
								<option value="8+">8+ Flüge</option>
							</select>
							<ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] w-5 h-5 pointer-events-none" />
						</div>
						{errors.amount_flights && (
							<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
								{errors.amount_flights}
							</p>
						)}
					</div>

					{/* Company - Only show for internal users */}
					{user?.internal_extra && (
						<div>
							<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
								{t("profileData.fields.company")}
							</label>
							<div className="relative">
								<select
									value={formData.company}
									onChange={(e) => handleInputChange("company", e.target.value)}
									className="w-full h-14 px-4 border border-[var(--ucm-border-light)] rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent appearance-none pr-10"
								>
									<option value="">
										{t("profileData.fields.companyPlaceholder")}
									</option>
									{COMPANY_OPTIONS.map((company) => (
										<option key={company} value={company}>
											{company}
										</option>
									))}
								</select>
								<ChevronDown
									className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] pointer-events-none"
									size={20}
								/>
							</div>
						</div>
					)}

					{/* Department - Only show for internal users */}
					{user?.internal_extra && (
						<div>
							<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
								{t("profileData.fields.department")}
							</label>
							<div className="relative">
								<input
									type="text"
									value={formData.department}
									onChange={(e) =>
										handleInputChange("department", e.target.value)
									}
									className="w-full h-14 px-4 border border-[var(--ucm-border-light)] rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent"
									placeholder={t("profileData.fields.departmentPlaceholder")}
								/>
							</div>
						</div>
					)}

					{/* Airport ID - Only show for internal users */}
					{user?.internal_extra && (
						<div>
							<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
								{t("profileData.fields.flughafenausweisnummer")}{" "}
								<span className="text-red-500">*</span>
							</label>
							<div className="relative">
								<input
									type="text"
									value={formData.flughafenausweisnummer}
									onChange={(e) =>
										handleInputChange("flughafenausweisnummer", e.target.value)
									}
									className={`w-full h-14 px-4 border rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent ${
										errors.flughafenausweisnummer
											? "border-red-500"
											: "border-[var(--ucm-border-light)]"
									}`}
									placeholder={t(
										"profileData.fields.flughafenausweisnummerPlaceholder"
									)}
									maxLength={50}
								/>
							</div>
							{errors.flughafenausweisnummer && (
								<p className="mt-1 text-sm text-red-500">
									{errors.flughafenausweisnummer}
								</p>
							)}
						</div>
					)}

					{/* Ausweis Color - Only show for internal users */}
					{user?.internal_extra && (
						<div>
							<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
								{t("profileData.fields.ausweisColor")}{" "}
								<span className="text-red-500">*</span>
							</label>
							<div className="relative">
								<select
									value={formData.ausweis_color}
									onChange={(e) =>
										handleInputChange("ausweis_color", e.target.value)
									}
									className={`w-full h-14 px-4 border rounded-xl bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent appearance-none pr-10 ${
										errors.ausweis_color
											? "border-red-500"
											: "border-[var(--ucm-border-light)]"
									}`}
								>
									<option value="">
										{t("profileData.fields.ausweisColorPlaceholder")}
									</option>
									{AUSWEIS_COLOR_OPTIONS.map((color) => (
										<option key={color} value={color}>
											{color}
										</option>
									))}
								</select>
								<ChevronDown
									className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] pointer-events-none"
									size={20}
								/>
							</div>
							{errors.ausweis_color && (
								<p className="mt-1 text-sm text-red-500">
									{errors.ausweis_color}
								</p>
							)}
						</div>
					)}

					{/* Occupation - Only show for external users */}
					{user?.internal_extra !== true && (
						<div>
							<label
								htmlFor="occupation"
								className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2"
							>
								Beschäftigung <span className="text-red-500">*</span>
							</label>
							<div className="relative">
								<select
									id="occupation"
									value={formData.main_occupation_id || ""}
									onChange={(e) =>
										handleInputChange(
											"main_occupation_id",
											e.target.value ? parseInt(e.target.value) : undefined
										)
									}
									className={`w-full h-14 px-4 border rounded-xl bg-white appearance-none transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent pr-10 ${
										errors.main_occupation_id
											? "border-[var(--ucm-error-red)]"
											: "border-[var(--ucm-border-light)]"
									}`}
								>
									<option value="">Bitte wählen</option>
									{occupationsLoading ? (
										<option disabled>Loading...</option>
									) : (
										occupations.map((occupation) => (
											<option key={occupation.id} value={occupation.id}>
												{occupation.name}
											</option>
										))
									)}
								</select>
								<ChevronDown
									className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] pointer-events-none"
									size={20}
								/>
							</div>
							{errors.main_occupation_id && (
								<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
									{errors.main_occupation_id}
								</p>
							)}
						</div>
					)}

					{/* Occupation proof button - Only for students */}
					{user?.internal_extra !== true && shouldShowOccupationProof() && (
						<div>
							<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
								{t("profileCompletion.occupationProof")}
							</label>
							<div
								onClick={navigateToOccupationProof}
								className="w-full px-4 py-3 border border-[var(--ucm-border-light)] rounded-xl cursor-pointer hover:border-[var(--ucm-primary-teal)] transition-colors flex items-center justify-between group"
							>
								<div className="flex items-center gap-3">
									{proofInfo ? (
										<>
											<FileText className="w-5 h-5 text-[var(--ucm-primary-teal)]" />
											<span className="text-[var(--ucm-text-dark)]">
												{proofInfo.file_name}
											</span>
										</>
									) : (
										<>
											<Upload className="w-5 h-5 text-[var(--ucm-text-medium)] group-hover:text-[var(--ucm-primary-teal)]" />
											<span className="text-[var(--ucm-text-medium)] group-hover:text-[var(--ucm-primary-teal)]">
												{t("profileCompletion.uploadOccupationProof")}
											</span>
										</>
									)}
								</div>
								<ChevronDown className="w-5 h-5 text-[var(--ucm-text-medium)] transform -rotate-90" />
							</div>
							{!proofInfo ? (
								<p className="mt-2 text-xs text-[var(--ucm-text-light)]">
									Bitte laden Sie den Nachweis hoch, um Termine auswählen zu
									können.
								</p>
							) : (
								<div
									className={`mt-2 px-3 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${
										proofInfo.status === "pending"
											? "bg-yellow-100 text-yellow-800"
											: proofInfo.status === "approved"
												? "bg-green-100 text-green-800"
												: "bg-red-100 text-red-800"
									}`}
								>
									{proofInfo.status === "approved" && (
										<CheckCircle className="w-3 h-3 text-green-800" />
									)}

									{proofInfo.status === "pending" && (
										<Clock className="w-3 h-3 text-yellow-800" />
									)}
									{proofInfo.status === "rejected" && (
										<XCircle className="w-3 h-3 text-red-800" />
									)}

									{proofInfo.status === "pending" && "Ausstehend"}
									{proofInfo.status === "approved" && "Genehmigt"}
									{proofInfo.status === "rejected" && "Abgelehnt"}
								</div>
							)}
						</div>
					)}

					{/* Agreement Checkboxes - Only for external users */}
					{user?.internal_extra !== true && (
						<div className="space-y-4 mb-6">
							<div className="flex items-center space-x-3">
								<input
									type="checkbox"
									id="accepts64DayRule"
									checked={formData.accepts_64_day_rule || false}
									onChange={(e) =>
										handleInputChange("accepts_64_day_rule", e.target.checked)
									}
									className="w-5 h-5 text-[var(--ucm-primary-teal)] border-[var(--ucm-border-light)] rounded focus:ring-[var(--ucm-primary-teal)] flex-shrink-0"
								/>
								<label
									htmlFor="accepts64DayRule"
									className="text-sm leading-5 text-[var(--ucm-text-dark)] flex items-center gap-2"
								>
									<span>
										Ich werde zum Zeitpunkt des Probebetriebs noch keine 64 Tage
										in kurzfristiger Beschäftigung absolviert haben.{" "}
										<span className="text-red-500">*</span>
									</span>
									<Info
										className="w-4 h-4 text-[var(--ucm-info-blue)] cursor-help flex-shrink-0"
										onClick={() => setShow64DayRuleInfoModal(true)}
									/>
								</label>
							</div>
							<div className="flex items-center space-x-3">
								<input
									type="checkbox"
									id="acknowledgesDataAccuracy"
									checked={acknowledgesDataAccuracy}
									onChange={(e) =>
										setAcknowledgesDataAccuracy(e.target.checked)
									}
									className="w-5 h-5 text-[var(--ucm-primary-teal)] border-[var(--ucm-border-light)] rounded focus:ring-[var(--ucm-primary-teal)] flex-shrink-0"
								/>
								<label
									htmlFor="acknowledgesDataAccuracy"
									className="text-sm leading-5 text-[var(--ucm-text-dark)]"
								>
									Ich bestätige, dass die obigen Angaben nach bestem Wissen
									korrekt sind. Bei Änderungen werde ich diese unverzüglich
									aktualisieren. <span className="text-red-500">*</span>
								</label>
							</div>
						</div>
					)}

					<button
						type="submit"
						disabled={
							updateLoading ||
							isLoadingData ||
							(user?.internal_extra !== true &&
								(!formData.accepts_64_day_rule || !acknowledgesDataAccuracy))
						}
						className="w-full h-14 bg-[var(--ucm-primary-teal)] text-white font-semibold rounded-full hover:bg-[var(--ucm-primary-teal-hover)] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
					>
						{updateLoading ? (
							<>
								<Loader2 className="w-5 h-5 animate-spin mr-2" />
								{t("profileInformation.savingProfile")}
							</>
						) : (
							t("common.continue")
						)}
					</button>
				</form>
			</AuthContainer>

			{/* Navigation Warning Modal */}
			{showNavigationWarningModal && (
				<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
					<div className="bg-white rounded-2xl p-6 max-w-md w-full">
						<h2 className="text-xl font-bold text-[var(--ucm-text-dark)] mb-4">
							Änderungen speichern?
						</h2>
						<p className="text-[var(--ucm-text-medium)] mb-6">
							Sie haben ungespeicherte Änderungen. Möchten Sie diese speichern,
							bevor Sie zur{" "}
							{navigationTarget === "work-permit-proof"
								? "Arbeitserlaubnis"
								: "Beschäftigungsnachweis"}
							-Upload Seite navigieren?
						</p>
						<div className="flex gap-3">
							<button
								onClick={() => setShowNavigationWarningModal(false)}
								className="flex-1 px-4 py-2 border border-[var(--ucm-border-medium)] rounded-xl text-[var(--ucm-text-dark)] hover:bg-gray-50 transition-colors"
							>
								{t("common.cancel")}
							</button>
							<button
								onClick={handleSaveAndNavigate}
								disabled={updateLoading}
								className="flex-1 px-4 py-2 bg-[var(--ucm-primary-teal)] text-white rounded-xl hover:bg-[var(--ucm-primary-teal-hover)] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
							>
								{updateLoading ? (
									<span className="flex items-center justify-center">
										<Loader2 className="w-4 h-4 animate-spin mr-2" />
										Speichern...
									</span>
								) : (
									"Speichern und fortfahren"
								)}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Nationality Change Confirmation Modal */}
			{showNationalityChangeModal && (
				<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
					<div className="bg-white rounded-2xl p-6 max-w-md w-full">
						<h2 className="text-xl font-bold text-[var(--ucm-text-dark)] mb-4">
							Nationalität ändern
						</h2>
						<p className="text-[var(--ucm-text-medium)] mb-6">
							Wenn Sie Ihre Nationalität ändern, wird Ihre vorhandene
							Arbeitserlaubnis gelöscht.{" "}
							{pendingNationalityId &&
							countries.find((c) => c.country_id === pendingNationalityId)
								?.is_eu
								? "Als EU-Bürger benötigen Sie keine Arbeitserlaubnis mehr."
								: "Sie müssen eine neue Arbeitserlaubnis für die ausgewählte Nationalität hochladen."}
						</p>
						<div className="flex gap-3">
							<button
								onClick={handleCancelNationalityChange}
								className="flex-1 px-4 py-2 border border-[var(--ucm-border-medium)] rounded-xl text-[var(--ucm-text-dark)] hover:bg-gray-50 transition-colors"
							>
								{t("common.cancel")}
							</button>
							<button
								onClick={handleConfirmNationalityChange}
								className="flex-1 px-4 py-2 bg-[var(--ucm-warning-orange)] text-white rounded-xl hover:bg-opacity-90 transition-colors"
							>
								Änderung bestätigen
							</button>
						</div>
					</div>
				</div>
			)}

			{/* ID Number Info Modal */}
			{showIdInfoModal && (
				<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
					<div className="bg-white rounded-2xl p-6 max-w-md w-full">
						<h2 className="text-xl font-bold text-[var(--ucm-text-dark)] mb-4">
							Hinweis zur ID-Nummer
						</h2>
						<div className="space-y-4 text-[var(--ucm-text-medium)]">
							<p>
								Sie können hier jede gültige Ausweisnummer eingeben
								(Personalausweis oder Reisepass). Der deutsche Personalausweis
								wird jedoch bevorzugt.
							</p>
							<p className="font-medium">
								Wichtig: Bitte bringen Sie zum Probebetrieb genau das Dokument
								mit, dessen Nummer Sie hier eingeben. Dies wird zur
								Verifizierung benötigt.
							</p>
						</div>
						<div className="mt-6">
							<button
								onClick={() => setShowIdInfoModal(false)}
								className="w-full px-4 py-2 bg-[var(--ucm-primary-teal)] text-white rounded-xl hover:bg-opacity-90 transition-colors"
							>
								Verstanden
							</button>
						</div>
					</div>
				</div>
			)}

			{/* 64-Day Rule Info Modal */}
			{show64DayRuleInfoModal && (
				<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
					<div className="bg-white rounded-2xl p-6 max-w-md w-full">
						<h2 className="text-xl font-bold text-[var(--ucm-text-dark)] mb-4">
							64-Tage-Regel für kurzfristige Beschäftigung
						</h2>
						<div className="space-y-4 text-[var(--ucm-text-medium)]">
							<p>
								Kurzfristige Beschäftigungen sind grundsätzlich auf maximal{" "}
								<strong>70 Arbeitstage</strong> pro Kalenderjahr begrenzt.
							</p>
							<p>
								Nach Abzug der gesetzlich zustehenden Urlaubstage (6 Tage bei
								einer 5-Tage-Woche) verbleiben <strong>64 Arbeitstage</strong>,
								die Sie im Jahr kurzfristig beschäftigt sein dürfen.
							</p>
							<p className="font-medium">
								Diese Regelung stellt sicher, dass kurzfristige Beschäftigungen
								sozialversicherungsfrei bleiben und gilt für alle kurzfristigen
								Tätigkeiten zusammen, nicht nur für ucm.
							</p>
						</div>
						<div className="mt-6">
							<button
								onClick={() => setShow64DayRuleInfoModal(false)}
								className="w-full px-4 py-2 bg-[var(--ucm-primary-teal)] text-white rounded-xl hover:bg-opacity-90 transition-colors"
							>
								Verstanden
							</button>
						</div>
					</div>
				</div>
			)}
		</>
	);
}
